import { moveToFulfilled, update } from '@ms/yammer-data-support';
import type { ThreadsAppliedTopicNamesClientsVariables } from '@ms/yammer-graphql';
import type { Reducer } from 'redux';

import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type { ThreadsAppliedTopicNamesLoadFulfilledAction } from '../../domains/threadsAppliedTopicNames/actions.types';
import type { Feed, Thread } from '../types';

import type { ThreadsAwaitingTopicName } from './types';

type FeedOfficeTopicsAwaitingNameAction =
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction;

type ThreadOfficeTopicsAwaitingNameAction =
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction;

export type ThreadsAwaitingTopicNameAction =
  | FeedOfficeTopicsAwaitingNameAction
  | ThreadOfficeTopicsAwaitingNameAction
  | NestedThreadListLoadFulfilledAction
  | ThreadsAppliedTopicNamesLoadFulfilledAction;

const initialState: ThreadsAwaitingTopicName = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithThreadsAwaitingTopicName = (
  state: ThreadsAwaitingTopicName,
  threads: Record<string, Thread> | undefined,
  entityId: Thread['id'] | Feed['id']
) => {
  if (!threads) {
    return state;
  }

  const threadIdsKeys = Object.keys(threads);
  const threadIdsKeysWithTopicIds = threadIdsKeys.filter(
    (threadId: Thread['id']) => threads[threadId]?.topicIds.length > 0
  );

  if (threadIdsKeysWithTopicIds.length === 0) {
    return state;
  }

  const existingThreadIds = state.fulfilled.byId[entityId]?.threadIds || [];
  if (!existingThreadIds.length) {
    return moveToFulfilled(state, { [entityId]: { id: entityId, threadIds: threadIdsKeysWithTopicIds } });
  }

  return update(state, entityId, { threadIds: [...existingThreadIds, ...threadIdsKeysWithTopicIds] });
};

const removeThreadIds = (
  state: ThreadsAwaitingTopicName,
  entityId: Thread['id'] | Feed['id'],
  threadIdsToRemove: ThreadsAppliedTopicNamesClientsVariables['ids']
) => {
  const existingThreadIds = state.fulfilled.byId[entityId]?.threadIds;

  const updatedThreadIds = existingThreadIds.filter((threadId) => !threadIdsToRemove.includes(threadId));

  return update(state, entityId, { threadIds: updatedThreadIds });
};

const reducer: Reducer<ThreadsAwaitingTopicName, ThreadsAwaitingTopicNameAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return stateWithThreadsAwaitingTopicName(state, action.payload.entities.threads, action.payload.id);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithThreadsAwaitingTopicName(state, action.payload.entities.threads, action.payload.id);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return stateWithThreadsAwaitingTopicName(state, action.payload.entities.threads, action.payload.id);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return stateWithThreadsAwaitingTopicName(state, action.payload.entities.threads, action.payload.threadId);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return stateWithThreadsAwaitingTopicName(state, action.payload.entities.threads, action.payload.threadId);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return stateWithThreadsAwaitingTopicName(state, action.payload.entities.threads, action.payload.threadId);
    case 'THREADS_APPLIED_TOPIC_NAMES.LOAD_FULFILLED':
      return removeThreadIds(state, action.payload.id, action.payload.threadIds);
    default:
      return state;
  }
};

export default reducer;
